import dynamic from "next/dynamic";
import { Mic, Radical, Square, X } from "lucide-react";
import LazyLoaded from "../LazyLoaded";
import { Paperclip } from "lucide-react";
import { Editor } from "@knowt/editor/react";
import { CodeXml, Spline } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";
import {
    fileTypeFromMimeType,
    generalizedMediaTypeFromSingleFileType,
    SUPPORTED_CHAT_ATTACHMENT_MIME_TYPES,
} from "@knowt/syncing/utils/fileTypeUtils";
import { useS3UploadJobsSelector } from "@knowt/syncing/context/S3UploadJobContext";
import Mountable from "../styled/div/Mountable";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { assertTruthy } from "@knowt/syncing/utils/assertions";
import { ChatEditorToolbarBtn } from "./styled/RichEditorResponsesBtns";
import useAudioRecorder from "@/features/AudioRecording/useAudioRecorder";
import { MediaType } from "@knowt/syncing/graphql/schema";
import { FlexRowAlignCenter } from "../Flex";
import { LiveAudioVisualizer } from "@/features/AudioRecording/LiveAudioVisualizer";
import { themeColors } from "@/utils/themeColors";
import { spacing } from "@/utils/spacing";

const WhiteboardPopup = dynamic(() => import("@/components/Tldraw/WhiteBoardPopup"), { ssr: false });

type RichEditorResponseToolbarProps = {
    editor: Editor | null;
    localEditorAttachments: LocalMessageAttachment[] | null;
    handleUpdateAttachments?: (attachments: LocalMessageAttachment[] | null) => void | null;
    btnStyle?: React.CSSProperties;
    confirmFileUpload?: ({
        file,
        name,
        extension,
        customBucketName,
        ignoreMediaCreation,
    }: {
        file: File;
        name: string;
        extension?: string;
        customBucketName: string;
        ignoreMediaCreation: boolean;
    }) => Promise<{ id: string; extension: string }>;
    filesS3BucketName?: string;
    stretchToolbar?: (isFullWidth: boolean) => void;
};

export const RichEditorResponseToolbar = ({
    editor,
    localEditorAttachments,
    handleUpdateAttachments,
    filesS3BucketName,
    btnStyle,
    stretchToolbar,
}: RichEditorResponseToolbarProps) => {
    const { userId } = useCurrentUser();
    const [isWhiteBoardOpen, setIsWhiteBoardOpen] = useState(false);

    const confirmFileUpload = useS3UploadJobsSelector(s => s.confirmUpload);
    const isDisabled = useS3UploadJobsSelector(s => s.isDisabled);
    const [isRecordingAudio, setIsRecordingAudio] = useState(false);

    const [isRecordingComplete, setIsRecordingComplete] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const recorder = useAudioRecorder({
        noiseSuppression: true,
        echoCancellation: true,
    });

    const { mediaRecorder, recordingBlob } = recorder;

    const handleAddCodeBlock = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor?.commands.insertContent({ type: "paragraph" });
            editor?.chain().focus().toggleCodeBlock().run();
        },
        [editor]
    );

    const handleAddWhiteboardToLocalAttachments = useCallback(
        (whiteboardAttachment: LocalMessageAttachment) => {
            const newAttachments = [...(localEditorAttachments || []), whiteboardAttachment];
            assertTruthy(handleUpdateAttachments, "handleUpdateAttachments is required");
            handleUpdateAttachments(newAttachments);
            setIsWhiteBoardOpen(false);
        },
        [localEditorAttachments, handleUpdateAttachments]
    );

    const handleFileInputChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];

            if (!file) return;

            const { id, bucket, extension } = await confirmFileUpload({
                file,
                name: file.name,
                userId,
                ignoreMediaCreation: true,
            });

            const newAttachments = [
                ...(localEditorAttachments || []),
                {
                    id,
                    name: file.name,
                    type: generalizedMediaTypeFromSingleFileType(extension),
                    url: `https://${bucket}.s3.amazonaws.com/${id}.${fileTypeFromMimeType(file.type)}`,
                },
            ];

            assertTruthy(handleUpdateAttachments, "handleUpdateAttachments is required");

            handleUpdateAttachments(newAttachments);
        },
        [confirmFileUpload, localEditorAttachments, handleUpdateAttachments, userId]
    );

    const handleAudioInputClick = () => {
        setIsRecordingAudio(true);
        stretchToolbar?.(true);
        recorder.startRecording();
    };

    const handleStopAudioRecording = () => {
        recorder.stopRecording();
        setIsRecordingAudio(false);
        stretchToolbar?.(false);
    };

    const handleConfirmAudioRecording = async () => {
        setIsRecordingComplete(true);
        handleStopAudioRecording();
    };

    useEffect(() => {
        const handleConfirmAudioRecording = async () => {
            if (recordingBlob && isRecordingComplete) {
                const audioUrl = await confirmFileUpload({
                    file: new File([recordingBlob], "audio.mp3", { type: "audio/mp3" }),
                    name: "audio.mp3",
                    extension: "mp3",
                    customBucketName: filesS3BucketName,
                    ignoreMediaCreation: true,
                });
                const newAttachments = [
                    ...(localEditorAttachments || []),
                    {
                        id: audioUrl.id,
                        name: "audio.mp3",
                        type: MediaType.AUDIO,
                        url: `https://${filesS3BucketName}.s3.amazonaws.com/${audioUrl.id}.mp3`,
                    },
                ];
                handleUpdateAttachments?.(newAttachments);
                setIsRecordingComplete(false);
            }
        };

        handleConfirmAudioRecording();
    }, [recordingBlob, isRecordingComplete]);

    if (isRecordingAudio) {
        return (
            <FlexRowAlignCenter style={{ width: "100%", justifyContent: "space-between", gap: spacing.SM }}>
                <ChatEditorToolbarBtn
                    id="cancel-audio-input-button"
                    icon={<X size={18} />}
                    onClick={handleStopAudioRecording}
                />
                {mediaRecorder && (
                    <FlexRowAlignCenter style={{ width: "100%" }}>
                        <LiveAudioVisualizer
                            mediaRecorder={mediaRecorder}
                            barWidth={3}
                            gap={2}
                            width={300}
                            height={12}
                            fftSize={128}
                            maxDecibels={-10}
                            minDecibels={-80}
                            barColor={themeColors.neutral3}
                            smoothingTimeConstant={0.8}
                            canvasStyle={{ width: "100%" }}
                        />
                    </FlexRowAlignCenter>
                )}
                <ChatEditorToolbarBtn
                    id="stop-audio-input-button"
                    icon={<Square size={18} fill={themeColors.pureWhite} color={themeColors.pureWhite} />}
                    onClick={handleConfirmAudioRecording}
                    style={{ backgroundColor: themeColors.errorPrimary }}
                />
            </FlexRowAlignCenter>
        );
    }

    return (
        <>
            <Mountable mount={!isDisabled}>
                <ChatEditorToolbarBtn
                    id="file-attachment-button"
                    onClick={() => fileInputRef.current?.click()}
                    style={btnStyle}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        // image and audio files
                        accept={SUPPORTED_CHAT_ATTACHMENT_MIME_TYPES.join(",")}
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                        multiple={false}
                        data-testid="auto-id-9640281f-21c3-410c-8482-83ef428ec1d5"
                    />
                    <Paperclip size={18} />
                </ChatEditorToolbarBtn>
            </Mountable>
            <ChatEditorToolbarBtn
                id="code-block-button"
                icon={<CodeXml size={18} />}
                onClick={handleAddCodeBlock}
                style={btnStyle}
            />
            <ChatEditorToolbarBtn
                id="math-block-button"
                icon={<Radical size={18} />}
                onClick={() => {
                    editor?.commands.toggleMathematics();
                }}
                style={btnStyle}
            />
            <Mountable mount={!isDisabled}>
                <ChatEditorToolbarBtn
                    id="whiteboard-button"
                    icon={<Spline size={18} />}
                    onClick={() => setIsWhiteBoardOpen(true)}
                    style={btnStyle}
                />
                <ChatEditorToolbarBtn
                    id="audio-input-button"
                    icon={<Mic size={18} />}
                    onClick={handleAudioInputClick}
                    style={btnStyle}
                />
            </Mountable>
            <LazyLoaded load={isWhiteBoardOpen}>
                <WhiteboardPopup
                    isOpen={isWhiteBoardOpen}
                    onClose={() => setIsWhiteBoardOpen(false)}
                    onWhiteboardUsageConfirm={handleAddWhiteboardToLocalAttachments}
                />
            </LazyLoaded>
        </>
    );
};

export default RichEditorResponseToolbar;
